// src/Pages/ApiKeys/CreateApiKeyModal.tsx

import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectItem,
  TextInput,
  MultiSelect,
  MultiSelectItem,
} from "@tremor/react";
import { KeyIcon } from "lucide-react";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import type { ApiKeyCreateRequest } from "@/types/apiKeys";

interface CreateApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ApiKeyCreateRequest) => Promise<any>;
  preSelectedOrgId?: number;
  loading?: boolean;
}

interface Organization {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
}

const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  open,
  onClose,
  onSubmit,
  preSelectedOrgId,
  loading = false,
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState<ApiKeyCreateRequest>({
    org_id: preSelectedOrgId || 0,
    name: "",
    description: "",
    allowed_products: undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const axiosPrivate = useAxiosPrivate();

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axiosPrivate.get("/auth/orgs");
        setOrganizations(response.data.data);
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
      }
    };
    
    if (open && !preSelectedOrgId) {
      fetchOrganizations();
    }
  }, [open, preSelectedOrgId, axiosPrivate]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axiosPrivate.get("/auth/products");
        setProducts(response.data.products);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      }
    };
    
    if (open) {
      fetchProducts();
    }
  }, [open, axiosPrivate]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        org_id: preSelectedOrgId || 0,
        name: "",
        description: "",
        allowed_products: undefined,
      });
      setErrors({});
    }
  }, [open, preSelectedOrgId]);

  const handleInputChange = (field: keyof ApiKeyCreateRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.org_id || formData.org_id === 0) {
      newErrors.org_id = "Παρακαλώ επιλέξτε οργανισμό";
    }
    
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Το όνομα είναι υποχρεωτικό";
    } else if (formData.name.length < 3) {
      newErrors.name = "Το όνομα πρέπει να έχει τουλάχιστον 3 χαρακτήρες";
    } else if (formData.name.length > 100) {
      newErrors.name = "Το όνομα δεν μπορεί να υπερβαίνει τους 100 χαρακτήρες";
    }
    
    if (formData.description && formData.description.length > 255) {
      newErrors.description = "Η περιγραφή δεν μπορεί να υπερβαίνει τους 255 χαρακτήρες";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Clean up the data before submission
      const submitData: ApiKeyCreateRequest = {
        org_id: formData.org_id,
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        allowed_products: formData.allowed_products && formData.allowed_products.length > 0 
          ? formData.allowed_products 
          : undefined,
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error: any) {
      // Error handling is done in the parent component/hook
      console.error("Failed to create API key:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <KeyIcon className="h-5 w-5" />
            <span>Δημιουργία Νέου API Key</span>
          </DialogTitle>
          <DialogDescription>
            Δημιουργήστε ένα νέο API key για έναν οργανισμό. Το key θα εμφανιστεί μόνο μία φορά.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {!preSelectedOrgId && (
            <div className="space-y-2">
              <Label htmlFor="organization">
                Οργανισμός <span className="text-red-500">*</span>
              </Label>
              <Select
                id="organization"
                value={formData.org_id.toString()}
                onValueChange={(value) => handleInputChange("org_id", Number.parseInt(value))}
                error={!!errors.org_id}
                errorMessage={errors.org_id}
                placeholder="Επιλέξτε οργανισμό"
              >
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id.toString()}>
                    {org.name}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">
              Όνομα API Key <span className="text-red-500">*</span>
            </Label>
            <TextInput
              id="name"
              placeholder="π.χ. Production API"
              value={formData.name}
              onValueChange={(value) => handleInputChange("name", value)}
              error={!!errors.name}
              errorMessage={errors.name}
              required
            />
            <p className="text-xs text-gray-500">
              Ένα περιγραφικό όνομα για την αναγνώριση του API key
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Περιγραφή</Label>
            <TextInput
              id="description"
              placeholder="π.χ. Για automated deployment"
              value={formData.description || ""}
              onValueChange={(value) => handleInputChange("description", value)}
              error={!!errors.description}
              errorMessage={errors.description}
            />
            <p className="text-xs text-gray-500">
              Προαιρετική περιγραφή για τη χρήση του API key
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="allowed_products">Επιτρεπόμενα Προϊόντα</Label>
            <MultiSelect
              id="allowed_products"
              value={formData.allowed_products?.map(String) || []}
              onValueChange={(values) => 
                handleInputChange("allowed_products", values.map(Number))
              }
              placeholder="Όλα τα προϊόντα (προεπιλογή)"
            >
              {products.map(product => (
                <MultiSelectItem key={product.id} value={product.id.toString()}>
                  {product.name}
                </MultiSelectItem>
              ))}
            </MultiSelect>
            <p className="text-xs text-gray-500">
              Περιορίστε την πρόσβαση σε συγκεκριμένα προϊόντα (προαιρετικό)
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-blue-800">
              <strong>Σημείωση:</strong> Ο οργανισμός μπορεί να έχει μέχρι 10 ενεργά API keys. 
              Το νέο key θα εμφανιστεί μόνο μία φορά μετά τη δημιουργία.
            </p>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Δημιουργία..." : "Δημιουργία API Key"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateApiKeyModal;